/**
 * Ledger route factory. The shell spreads these under the authed route:
 *
 *   children: [...ledgerRoutes(authedRoute)]
 *
 * Pages stay router-free; this file is the only place ledger code touches the
 * router and the only place it reads the session. Controls are gated on the
 * exact permission the generated route policy demands.
 */
import { createRoute, type AnyRoute } from "@tanstack/react-router";

import { useSession } from "@/lib/session/use-session";

import { AccountsPage } from "./pages/accounts-page";
import { JournalsPage } from "./pages/journals-page";
import { FxRatesPage } from "./pages/fx-rates-page";
import { FiscalCalendarPage } from "./pages/fiscal-calendar-page";
import { TrialBalancePage } from "./pages/trial-balance-page";
import { CoaImportPage } from "./pages/coa-import-page";
import { FailedEventsPage } from "./pages/failed-events-page";
import { PostingRulesPage } from "./pages/posting-rules-page";
import { LEDGER_PERMISSIONS as P, LedgerPermissionBoundary } from "./permissions";

function AccountsRoute() {
  const { hasPermission } = useSession();
  return (
    <LedgerPermissionBoundary permission={P.accountRead}>
      <AccountsPage
        canCreate={hasPermission(P.accountCreate)}
        canUpdate={hasPermission(P.accountUpdate)}
      />
    </LedgerPermissionBoundary>
  );
}

function JournalsRoute() {
  const { hasPermission, company } = useSession();
  return (
    <LedgerPermissionBoundary permission={P.journalRead}>
      {/* The manual-journal form posts in the company's base currency, which
          the session publishes. A company with no ledger settings has none,
          and the page says so rather than posting in a guessed currency. */}
      <JournalsPage
        currency={company?.base_currency ?? null}
        canPost={hasPermission(P.journalPost)}
        canReverse={hasPermission(P.journalReverse)}
      />
    </LedgerPermissionBoundary>
  );
}

function FxRatesRoute() {
  const { hasPermission } = useSession();
  return (
    <LedgerPermissionBoundary permission={P.fxRateRead}>
      <FxRatesPage canCreateManual={hasPermission(P.fxRateManualCreate)} />
    </LedgerPermissionBoundary>
  );
}

function FiscalCalendarRoute() {
  const { hasPermission } = useSession();
  return (
    <LedgerPermissionBoundary permission={P.fiscalPeriodRead}>
      <FiscalCalendarPage
        canTransition={hasPermission(P.fiscalPeriodTransition)}
        canGenerate={hasPermission(P.fiscalCalendarGenerate)}
      />
    </LedgerPermissionBoundary>
  );
}

function TrialBalanceRoute() {
  return (
    <LedgerPermissionBoundary permission={P.trialBalanceRead}>
      <TrialBalancePage />
    </LedgerPermissionBoundary>
  );
}

function PostingRulesRoute() {
  const { hasPermission } = useSession();
  return (
    <LedgerPermissionBoundary permission={P.postingRuleRead}>
      <PostingRulesPage
        canCreate={hasPermission(P.postingRuleCreate)}
        canUpdate={hasPermission(P.postingRuleUpdate)}
        canPreview={hasPermission(P.postingRulePreview)}
        canReadAccounts={hasPermission(P.accountRead)}
      />
    </LedgerPermissionBoundary>
  );
}

function CoaImportRoute() {
  const { hasPermission } = useSession();
  return (
    <LedgerPermissionBoundary permission={P.accountImportPreview}>
      <CoaImportPage
        canPreview={hasPermission(P.accountImportPreview)}
        canApply={hasPermission(P.accountImport)}
      />
    </LedgerPermissionBoundary>
  );
}

function FailedEventsRoute() {
  const { hasPermission } = useSession();
  return (
    <LedgerPermissionBoundary permission={P.businessEventRead}>
      <FailedEventsPage canReplay={hasPermission(P.businessEventReplay)} />
    </LedgerPermissionBoundary>
  );
}

export function ledgerRoutes(parent: AnyRoute): AnyRoute[] {
  return [
    createRoute({
      getParentRoute: () => parent,
      path: "ledger/accounts",
      component: AccountsRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "ledger/journals",
      component: JournalsRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "ledger/trial-balance",
      component: TrialBalanceRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "ledger/posting-rules",
      component: PostingRulesRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "ledger/fx-rates",
      component: FxRatesRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "ledger/accounts/import",
      component: CoaImportRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "ledger/failed-events",
      component: FailedEventsRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "ledger/fiscal-periods",
      component: FiscalCalendarRoute,
    }),
  ] as AnyRoute[];
}
